import { RELATED_PERSON_ASSOCIATION } from '@constants/extension-codes';

import type {
  ContactModel,
  RelatedPersonModel,
  RelatedPersonModelInput,
} from 'generated/types';

import { Contact } from './contact';
import { FhirResource } from './fhir-resource';

export type RelatedPersonType =
  | 'emergency-contact'
  | 'legal-counsel'
  | 'power-of-attorney'
  | 'secondary-emergency-contact';

export type RelatedPersonValues = Pick<
  RelatedPersonRecord,
  | 'address'
  | 'address2'
  | 'association'
  | 'city'
  | 'country'
  | 'email'
  | 'first_name'
  | 'last_name'
  | 'phone'
  | 'postalCode'
  | 'region'
>;
export type FormValues = Omit<
  ContactModel,
  'id' | 'postal_code' | 'resource_type' | 'role_code' | 'role_name'
> & {
  contact_id?: string | null;
  postalCode?: string;
  submit: string | null;
  association?: string;
  // Care team participant type: Practitioner or Organization
  type?: string | null;
  /** Care team org type (e.g. Healthcare Provider) */
  org_type?: string | null;
  roles: {
    role_code: string;
    role_name: string;
  }[];
  search?: {
    filter?: string | null;
    text?: string | null;
    has_queried: boolean;
    no_match?: boolean | null;
  };
};

export class RelatedPersonRecord extends Contact {
  public model: RelatedPersonModel;

  public constructor(model: RelatedPersonModel) {
    super(model);
    this.model = model;
  }

  public get relationship() {
    const relationships = this.model.relationship?.reduce<
      { role_code: string; role_name: string }[]
    >((acc, { coding }) => {
      const role_code = coding?.map(({ code }) => code).join('/');
      const role_name = coding?.map(({ display }) => display).join('/');
      if (role_code && role_name) {
        acc.push({ role_code, role_name });
      }
      return acc;
    }, []);

    return relationships;
  }

  /**
   * Used only for emergency contacts
   * Association: Relationship between emergency contact and patient (e.g. Spouse)
   */
  public get association() {
    const extension = this.findExtension(RELATED_PERSON_ASSOCIATION);
    return extension?.valueString;
  }

  public set association(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: RELATED_PERSON_ASSOCIATION,
        valueString: value,
      });
    } else {
      this.removeExtension(RELATED_PERSON_ASSOCIATION);
    }
  }

  public isLegalCounsel() {
    const relations = this.model.relationship ?? [];
    const isCounsel = relations.some((rel) =>
      rel.coding?.some((r) => r.code === 'legal-counsel')
    );
    return isCounsel;
  }

  public isPowerOfAttorney() {
    const relations = this.model.relationship ?? [];
    const isPOA = relations.some((rel) =>
      rel.coding?.some(
        (r) =>
          r.code === 'power-of-attorney' ||
          r.code === 'POWATT' ||
          r.code === 'DPOWATT' ||
          r.code === 'HPOWATT' ||
          r.code === 'SPOWAT'
      )
    );
    return isPOA;
  }

  public isEmergencyContact() {
    const relations = this.model.relationship ?? [];
    const isEmergContact = relations.some((rel) =>
      rel.coding?.some((r) => r.code === 'emergency-contact' || r.code === 'C')
    );
    return isEmergContact;
  }

  public isSecondaryEmergencyContact() {
    const relations = this.model.relationship ?? [];
    const isEmergContact = relations.some((rel) =>
      rel.coding?.some(
        (r) => r.code === 'secondary-emergency-contact' || r.code === 'C'
      )
    );
    return isEmergContact;
  }

  /**
   * Find all extensions of a specific type
   * @param code Extension code
   * @returns All extensions matching the given code
   */
  protected findExtensions(code: string) {
    const extension = this.model.extension?.filter((e) => e.url.endsWith(code));
    return extension;
  }

  public getModelInput(values?: RelatedPersonValues): RelatedPersonModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return cleaned;
  }
}
